import { Alert, Box, Button, Center, Group, Menu, Stack, Tabs, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconDownload, IconMail, IconPalette, IconSearch } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import queryString from 'query-string';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import PageSizeOptions from '../constants/pageSizeOptions';
import { Page } from '../constants/pageTitles';
import { dateToEndOfDay, dateToStartOfDay, daysAgoISODate, todayISODate } from '../helpers/dateUtils';
import { generatedCouponLabels } from '../helpers/platformLabel';
import useFetch from '../helpers/useFetch';
import GeneratedCouponsEmptyState from './Coupons/GeneratedCouponsEmptyState';
import CredentialsAlert from './CredentialsAlert';
import SenderVerificationAlert from './SenderVerificationAlert';
import EmailSendingHoldAlert from './EmailSendingHoldAlert';
import ErrorMessage from './ErrorMessage';
import Metadata from './Metadata';
import MetricsSelector from './MetricsSelector';
import NewElementButton from './NewElementButton';
import PageFilter from './PageFilter';
import Pager from './Pager/Pager';
import SearchBar from './SearchBar';
import Spinner from './Spinner';
import WhatsappImportModal from './Whatsapp/WhatsappImportModal';

const statsDebounced = AwesomeDebouncePromise((fetch, accountId, domainId, startTs, endTs, entityType) => {
  return fetch(
    `/api/admin/accounts/${accountId}/domains/${domainId}/${entityType}Stats/${encodeURIComponent(startTs)}/${encodeURIComponent(endTs)}`
  );
}, 300);

const pluralize = (type) => {
  if (type === 'story') return 'stories';
  return `${type}s`;
};

const fetchDebounced = AwesomeDebouncePromise((fetch, accountId, domainId, entityType, filter) => {
  let processedType = entityType === 'facebookProductSet' ? 'facebook/productSet' : entityType;
  const plural = pluralize(processedType);
  const url = `/api/admin/accounts/${accountId}/domains/${domainId}/${plural}?${queryString.stringify(filter)}`;
  return fetch(url);
}, 300);

const EmptyState = ({ searchValue }) => (
  <Center py={80}>
    <Stack align="center" gap="md">
      <IconSearch size={48} stroke={1} color="var(--mantine-color-dimmed)" />
      <Stack align="center" gap="xs">
        <Title order={4}>No results found</Title>
        {searchValue && <Text c="dimmed">Try a different search term.</Text>}
      </Stack>
    </Stack>
  </Center>
);

const ListingPage = ({ entityType, Cards, useStats, filterBy, createRestrictionKey }) => {
  const navigate = useNavigate();
  const fetch = useFetch();
  const location = useLocation();
  const { accountId, domainId } = useParams();
  const facebook = useSelector((state) => state.facebook);
  const domain = useSelector((state) => state.domain);
  const googleAccount = useSelector((state) => state.google.account);
  const whatsappAccount = useSelector((state) => state.whatsapp.account);

  const selectedPageSize = parseInt(queryString.parse(location.search)?.resultsPerPage) || PageSizeOptions[0];
  const selectedPage = parseInt(queryString.parse(location.search)?.page) || 1;
  const searchValue = queryString.parse(location.search)?.searchTerms || '';

  const initialPeriodDays = 30;
  const [entities, setEntities] = useState();
  const [stats, setStats] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  // Store ISO date strings (YYYY-MM-DD)
  const [startDate, setStartDate] = useState(daysAgoISODate(initialPeriodDays - 1));
  const [endDate, setEndDate] = useState(todayISODate());
  const [pageCount, setPageCount] = useState();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showWhatsappImportModal, setShowWhatsappImportModal] = useState(false);
  const [couponCounts, setCouponCounts] = useState();

  const tabs = ['generalEmails', 'aiGeneratedEmails'];
  const search = queryString.parse(location.search);
  const tab = search.tab || tabs[0];
  const setTab = useCallback(
    (tab) => {
      navigate(`${location.pathname}?${queryString.stringify({ ...search, tab, page: 1 })}`);
    },
    [navigate, location.pathname, search]
  );

  const assetTabs = ['images', 'documents'];
  const assetTab = search.tab || assetTabs[0];
  const setAssetTab = useCallback(
    (tab) => {
      navigate(`${location.pathname}?${queryString.stringify({ ...search, tab, page: 1 })}`);
    },
    [navigate, location.pathname, search]
  );

  const isGeneratedCouponListing = entityType === 'coupon' && Boolean(domain.domain?.capabilities?.generatedCoupons);
  const generatedCouponLabel = generatedCouponLabels(domain.domain?.platform);
  const couponSources = ['generated', 'upload'];
  const couponSource = couponSources.includes(search.source) ? search.source : couponSources[0];
  const setCouponSource = (source) => {
    navigate(`${location.pathname}?${queryString.stringify({ ...search, source, page: 1 })}`);
  };

  const fetchEntities = useCallback(() => {
    setLoading(true);

    const filterDefaultValues = {
      page: 1,
      resultsPerPage: selectedPageSize,
      searchTerms: '',
    };
    const filter = {
      ...filterDefaultValues,
      ...queryString.parse(location.search),
      ...(entityType === 'coupon' ? { source: isGeneratedCouponListing ? couponSource : undefined } : {}),
    };
    fetchDebounced(fetch, accountId, domainId, entityType, filter)
      .then((response) => {
        setEntities(response[pluralize(entityType)]);
        setPageCount(response.pagesCount);
        setCouponCounts(response.counts);
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [domainId, accountId, fetch, entityType, location.search, selectedPageSize, isGeneratedCouponListing, couponSource]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  useEffect(() => {
    if (!location.search) {
      if (entityType === 'asset') {
        setAssetTab(assetTab);
      }
      if (entityType === 'email') {
        setTab(tab);
      }
    }
  }, [location.search, entityType, assetTab, setAssetTab, tab, setTab]);

  useEffect(() => {
    if (!useStats) {
      return;
    }
    setStats(undefined);
    if (startDate && endDate) {
      // Convert ISO date strings to full timestamps for the API (using local timezone)
      const startTs = dateToStartOfDay(startDate, 'local');
      const endTs = dateToEndOfDay(endDate, 'local');
      statsDebounced(fetch, accountId, domainId, startTs, endTs, entityType)
        .then((stats) => {
          setStats(stats);
        })
        .catch((error) => {
          setError(error);
        });
    }
  }, [accountId, domainId, startDate, endDate, fetch, entityType, useStats]);

  const setPageSize = async (pageSize) => {
    navigate({
      search: queryString.stringify({
        ...queryString.parse(location.search),
        page: 1,
        resultsPerPage: pageSize,
      }),
    });
  };

  const onSearchChange = (e) => {
    const newFilter = {
      ...queryString.parse(location.search),
      searchTerms: e.target.value.toLowerCase(),
      page: 1,
    };
    navigate({ search: queryString.stringify({ ...newFilter }) });
  };
  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  const handlePageClick = (e) => {
    const currentPage = e.selected;
    navigate({
      search: queryString.stringify({
        ...queryString.parse(location.search),
        page: currentPage,
      }),
    });
  };

  const handleToggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  if (entities === undefined) {
    return <Spinner />;
  }

  const showGeneratedCouponsEmptyState =
    isGeneratedCouponListing && Boolean(couponCounts) && couponCounts.generated + couponCounts.upload === 0;
  const navigateToNewCoupon = (source) => {
    navigate(`${location.pathname}/new${source === 'generated' ? '?source=generated' : ''}`);
  };

  const entityTypesWithDisabledExportButton = [
    'template',
    'asset',
    'facebookProductSet',
    'coupon',
    'whatsappMessage',
    'survey',
  ];
  return (
    <div>
      {entityType === 'facebookProductSet' && (facebook.user === null || facebook.user.businessId === null) && (
        <Alert color="blue" icon={<IconAlertTriangle size={16} />} mb="sm">
          Your Facebook account is not connected to Releva or you have not selected a business in which you want to sync
          the products.{' '}
          <a href={`#/accounts/${accountId}/domains/${domainId}/settings/meta?tab=facebook`}>
            You can configure these things from here first.
          </a>
        </Alert>
      )}
      {entityType === 'whatsappMessage' && (!whatsappAccount || !whatsappAccount.whatsappAccountId) && (
        <Alert color="blue" icon={<IconAlertTriangle size={16} />} mb="sm">
          Connect your WhatsApp Business Account (WABA) to enable the creation of template messages.{' '}
          <a href={`#/accounts/${accountId}/domains/${domainId}/settings/meta?tab=whatsApp`}>
            You can configure these things from here first.
          </a>
        </Alert>
      )}
      {entityType === 'googleAd' &&
        (googleAccount === null || googleAccount.adAccountId === null ? (
          <Alert color="blue" icon={<IconAlertTriangle size={16} />} mb="sm">
            Connect your Google Ad Account to enable the creation of Google ads.{' '}
            <a href={`#/accounts/${accountId}/domains/${domainId}/settings/google?tab=connect`}>
              You can set up and configure the necessary settings from here.
            </a>
          </Alert>
        ) : (
          !googleAccount.hasCustomerConversionGoals && (
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />} mb="sm">
              No customer conversion goals have been set up for this Google Ad Account.{' '}
              <a href="https://ads.google.com/aw/conversions" target="_blank" rel="noreferrer">
                Click here to set up conversion goals.
              </a>
            </Alert>
          )
        ))}

      {entityType === 'email' && (
        <>
          <SenderVerificationAlert />
          <EmailSendingHoldAlert accountId={domain.domain.accountId} domainId={domain.domain.id} />
        </>
      )}
      <Metadata title={Page[entityType]} />
      <Group mb="md" align="flex-end" wrap="wrap" gap="md">
        {useStats ? (
          <MetricsSelector
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            rowSize="12"
            scope={entityTypesWithDisabledExportButton.includes(entityType) ? '' : `${entityType}s`}
          />
        ) : null}

        {entityType === 'template' && (
          <>
            <PageFilter
              placeholder="Scope"
              queryKey="scope"
              options={[
                { value: 'web', label: ' Web' },
                { value: 'email', label: 'Email' },
                { value: 'search', label: 'Search' },
              ]}
            />
            <PageFilter
              placeholder="Usage"
              queryKey="used"
              options={[
                { value: 'true', label: 'Used' },
                { value: 'false', label: 'Not used' },
              ]}
            />
          </>
        )}

        {entityType === 'segment' && (
          <PageFilter
            placeholder="Scope"
            queryKey="scope"
            options={[
              { value: 'behavior', label: 'Behavior' },
              { value: 'profileFilter', label: 'Profile Filter' },
              { value: 'facebook', label: 'Facebook' },
              { value: 'google', label: 'Google' },
            ]}
          />
        )}

        {filterBy ? (
          <Box style={{ flex: 1, minWidth: 150 }}>
            <SearchBar onChange={onSearchChange} value={searchValue} />
          </Box>
        ) : null}

        {entityType === 'email' ? (
          <Menu opened={showDropdown} onChange={setShowDropdown} position="bottom-end">
            <Menu.Target>
              <div>
                <NewElementButton
                  onClick={handleToggleDropdown}
                  title={`New ${entityType}`}
                  restrictionKey={createRestrictionKey}
                />
              </div>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconMail size={14} />}
                onClick={() => {
                  handleToggleDropdown();
                  navigate(`/accounts/${accountId}/domains/${domainId}/emails/new?editor=stripo&tab=${tab}`);
                }}
              >
                Create Blank
              </Menu.Item>
              <Menu.Item
                leftSection={<IconPalette size={14} />}
                onClick={() => {
                  handleToggleDropdown();
                  navigate(`/accounts/${accountId}/domains/${domainId}/stripoTemplates`);
                }}
              >
                Browse Email Templates Gallery
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : entityType === 'facebookProductSet' && (facebook.user === null || facebook.user.businessId === null) ? (
          ''
        ) : entityType === 'asset' ? (
          <NewElementButton
            onClick={() => navigate(location.pathname + '/new' + location.search)}
            title={`New ${assetTab.slice(0, -1)}`}
            restrictionKey={createRestrictionKey}
          />
        ) : entityType === 'template' ? (
          <NewElementButton
            onClick={() => navigate(location.pathname + '/new' + location.search)}
            title="New Layout"
            restrictionKey={createRestrictionKey}
          />
        ) : entityType === 'textMessage' ? (
          <NewElementButton
            disabled={
              !domain.domain.textMessageSender ||
              (domain.domain.textMessageSender !== 'aws' &&
                (!domain.domain.textMessageSenderUser || !domain.domain.textMessageSenderPassword))
            }
            onClick={() => navigate(location.pathname + '/new' + location.search)}
            title="New Text Message"
            restrictionKey={createRestrictionKey}
          />
        ) : entityType === 'viberMessage' ? (
          <NewElementButton
            disabled={
              !domain.domain.viberMessageSender ||
              ((!domain.domain.viberMessageSenderUser || !domain.domain.viberMessageSenderPassword) &&
                (!domain.domain.viberMessageSenderUser ||
                  !domain.domain.viberMessageSenderPassword ||
                  !domain.domain.viberDisplayName ||
                  !domain.domain.viberSenderId))
            }
            onClick={() => navigate(location.pathname + '/new' + location.search)}
            title="New Viber Message"
            restrictionKey={createRestrictionKey}
          />
        ) : entityType === 'facebookAd' ? (
          <NewElementButton
            disabled={!facebook.user}
            onClick={() => navigate(location.pathname + '/new' + location.search)}
            title="New Facebook Ad"
            restrictionKey={createRestrictionKey}
          />
        ) : entityType === 'whatsappMessage' ? (
          <Group gap="xs" wrap="wrap">
            <Button
              variant="default"
              leftSection={<IconDownload size={14} />}
              disabled={!whatsappAccount || !whatsappAccount.whatsappAccountId}
              onClick={() => setShowWhatsappImportModal(true)}
            >
              Import templates
            </Button>
            <NewElementButton
              disabled={!whatsappAccount || !whatsappAccount.whatsappAccountId}
              onClick={() => navigate(location.pathname + '/new' + location.search)}
              title="New WhatsApp Message"
              restrictionKey={createRestrictionKey}
            />
          </Group>
        ) : isGeneratedCouponListing ? (
          !showGeneratedCouponsEmptyState && (
            <NewElementButton
              onClick={() => navigateToNewCoupon(couponSource)}
              title="uploadCoupons"
              label={couponSource === 'generated' ? generatedCouponLabel.create : undefined}
              restrictionKey={createRestrictionKey}
            />
          )
        ) : entityType === 'googleAd' ? (
          <NewElementButton
            disabled={!googleAccount || !googleAccount.adAccountId}
            onClick={() => navigate(location.pathname + '/new' + location.search)}
            title={`New ${entityType}`}
            restrictionKey={createRestrictionKey}
          />
        ) : (
          <NewElementButton
            onClick={() => navigate(location.pathname + '/new' + location.search)}
            title={`New ${entityType}`}
            restrictionKey={createRestrictionKey}
          />
        )}
      </Group>
      <CredentialsAlert entityType={entityType} />
      {loading ? (
        <Spinner />
      ) : isGeneratedCouponListing ? (
        showGeneratedCouponsEmptyState ? (
          <GeneratedCouponsEmptyState
            onCreateGeneratedCoupon={() => navigateToNewCoupon('generated')}
            onUploadCoupons={() => navigateToNewCoupon('upload')}
          />
        ) : (
          <>
            <Tabs value={couponSource} onChange={setCouponSource} mb="md">
              <Tabs.List>
                <Tabs.Tab value="generated">{generatedCouponLabel.tab}</Tabs.Tab>
                <Tabs.Tab value="upload">Uploaded Coupons</Tabs.Tab>
              </Tabs.List>
            </Tabs>
            {entities.length === 0 ? (
              <EmptyState searchValue={searchValue} />
            ) : (
              <Cards {...{ [`${entityType}s`]: entities, source: couponSource, refresh: fetchEntities }} />
            )}
          </>
        )
      ) : entityType === 'email' ? (
        <Tabs value={tab} onChange={setTab} mb="md">
          <Tabs.List>
            <Tabs.Tab value="generalEmails">Emails</Tabs.Tab>
            <Tabs.Tab value="aiGeneratedEmails">AI Generated Emails</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="generalEmails">
            {(() => {
              const filtered = entities.filter((entity) => entity.aiGenerated !== true);
              return filtered.length === 0 ? (
                <EmptyState searchValue={searchValue} />
              ) : (
                <Cards {...{ stats, [`${entityType}s`]: filtered, period: { startDate, endDate } }} />
              );
            })()}
          </Tabs.Panel>
          <Tabs.Panel value="aiGeneratedEmails">
            {(() => {
              const filtered = entities.filter((entity) => entity.aiGenerated === true);
              return filtered.length === 0 ? (
                <EmptyState searchValue={searchValue} />
              ) : (
                <Cards {...{ stats, [`${entityType}s`]: filtered, period: { startDate, endDate } }} />
              );
            })()}
          </Tabs.Panel>
        </Tabs>
      ) : entityType === 'asset' ? (
        <Tabs value={assetTab} onChange={setAssetTab} mb="md">
          <Tabs.List>
            <Tabs.Tab value="images">Images</Tabs.Tab>
            <Tabs.Tab value="documents">Documents</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="images">
            {(() => {
              const filtered = entities.filter((entity) => entity.type.startsWith('image'));
              return filtered.length === 0 ? (
                <EmptyState searchValue={searchValue} />
              ) : (
                <Cards {...{ [`${entityType}s`]: filtered }} />
              );
            })()}
          </Tabs.Panel>
          <Tabs.Panel value="documents">
            {(() => {
              const filtered = entities.filter((entity) => !entity.type.startsWith('image'));
              return filtered.length === 0 ? (
                <EmptyState searchValue={searchValue} />
              ) : (
                <Cards {...{ [`${entityType}s`]: filtered }} />
              );
            })()}
          </Tabs.Panel>
        </Tabs>
      ) : (
        <div data-tour="listingPageCards">
          {entities.length === 0 ? (
            <EmptyState searchValue={searchValue} />
          ) : (
            <Cards
              {...{
                stats,
                [`${entityType}s`]: entities,
                period: { startDate, endDate },
              }}
            />
          )}
        </div>
      )}

      <Pager
        pageSize={selectedPageSize}
        pageCount={pageCount}
        onPageChange={handlePageClick}
        initialPage={selectedPage}
        setPageSize={setPageSize}
      />

      {entityType === 'whatsappMessage' && (
        <WhatsappImportModal
          opened={showWhatsappImportModal}
          onClose={() => setShowWhatsappImportModal(false)}
          onImported={fetchEntities}
        />
      )}
    </div>
  );
};

export default ListingPage;
